import { format, isValid } from "date-fns";
import NotificationComp from "./notification";
import {
    useGetNotifications,
    useGetUnreadNotificationsCount,
    useMarkAllNotificationsAsRead,
    useMarkNotificationAsRead
} from "@/features/notifications/repositories/notifications-repository";
import { TNotification } from "@/types";

// Decision codes the API can put on a notification: `LegalReviewStatus`,
// `FinalDecisionDTO.DecisionStatus` and `LicenseStatus` all land in this field.
const APPROVED_DECISIONS = [
    "APPROVED",
    "APPROVED_WITH_CONDITIONS_INSPECTION",
    "LEGAL_REVIEW_COMPLETED",
    "INSPECTION_COMPLETED",
    "FINAL_COMPLETED"
];

const REJECTED_DECISIONS = [
    "REJECTED",
    "INSPECTION_REJECTED",
    "SUSPENDED",
    "EXPIRED"
];

function toStatus(notification: TNotification) {
    const decision = notification.decisionStatus?.toUpperCase();

    if (decision && APPROVED_DECISIONS.includes(decision)) return "approved" as const;
    if (decision && REJECTED_DECISIONS.includes(decision)) return "rejected" as const;

    return "pending" as const;
}

// `new Date(...)` on a malformed value yields an Invalid Date, which date-fns v4
// throws a RangeError on — so nothing reaches `format` unchecked.
function formatDate(value: string | undefined, pattern: string) {
    if (!value) return "";
    const parsed = new Date(value);
    return isValid(parsed) ? format(parsed, pattern) : "";
}

export default function NotificationsPanel() {
    const { notifications, isLoading, isError } = useGetNotifications({
        pager: { page: 0, size: 5 }
    });
    const { unreadCount } = useGetUnreadNotificationsCount();
    const { mutate: markAsRead } = useMarkNotificationAsRead();
    const { mutate: markAllAsRead, isPending: isMarkingAll } = useMarkAllNotificationsAsRead();

    const items = notifications?.results ?? [];

    return (
        <div className="py-1 px-5 mt-5 rounded-md shadow-sm bg-white md:block hidden">
            <div className="flex justify-between items-center gap-4 mb-4 mt-2">
                <h1 className="text-lg font-bold">
                    Notifications {unreadCount > 0 && <span className="text-primary">({unreadCount})</span>}
                </h1>
                {unreadCount > 0 && (
                    <button
                        type="button"
                        onClick={() => markAllAsRead()}
                        disabled={isMarkingAll}
                        className="text-sm text-primary underline disabled:opacity-50"
                    >
                        Mark all as read
                    </button>
                )}
            </div>
            {
                isLoading ? (
                    <div className="flex justify-center items-center py-10">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                    </div>
                ) : isError ? (
                    <p className="text-sm text-gray-500 py-6">Could not load your notifications.</p>
                ) : items.length === 0 ? (
                    <p className="text-sm text-gray-500 py-6">You have no notifications yet.</p>
                ) : (
                    items.map((notification: TNotification) => (
                        <NotificationComp
                            key={notification.id}
                            date={formatDate(notification.createdAt, "dd MMM")}
                            time={formatDate(notification.createdAt, "hh:mm a")}
                            title={notification.title}
                            message={notification.message ?? undefined}
                            status={toStatus(notification)}
                            read={notification.read}
                            onClick={notification.read ? undefined : () => markAsRead(notification.id)}
                        />
                    ))
                )
            }
        </div>
    )
}
